import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ExternalLink, BookOpen } from "lucide-react";

export default function Library() {
  const [entries, setEntries] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/library").then((r) => setEntries(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);

  const grouped = useMemo(() => {
    const filtered = entries.filter((e) =>
      !q.trim() ||
      e.title.toLowerCase().includes(q.toLowerCase()) ||
      e.summary.toLowerCase().includes(q.toLowerCase()) ||
      e.category.toLowerCase().includes(q.toLowerCase()));
    const map = new Map();
    for (const e of filtered) {
      if (!map.has(e.category)) map.set(e.category, []);
      map.get(e.category).push(e);
    }
    return Array.from(map.entries());
  }, [entries, q]);

  return (
    <div className="reveal">
      <div className="eyebrow">Research library</div>
      <h1 className="mt-1 text-3xl font-extrabold tracking-tight text-slate-900">Curated references</h1>
      <p className="mt-2 text-sm text-slate-600 max-w-2xl">
        Definitions, guidelines and landmark papers that underpin every recommendation in this app.
      </p>

      <div className="mt-6">
        <input
          type="search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search titles, categories, keywords…"
          className="w-full max-w-md h-10 px-3 rounded-sm border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
          data-testid="library-search-input"
        />
      </div>

      <div className="mt-8 space-y-10">
        {grouped.length === 0 && <div className="text-sm text-slate-500">No matching entries.</div>}
        {grouped.map(([cat, items]) => (
          <div key={cat}>
            <div className="flex items-center gap-2 text-slate-700">
              <BookOpen className="h-4 w-4" strokeWidth={1.75} />
              <div className="eyebrow">{cat}</div>
            </div>
            <div className="mt-3 grid md:grid-cols-2 gap-4">
              {items.map((e) => (
                <a key={e.title} href={e.url} target="_blank" rel="noopener noreferrer"
                   className="block border border-slate-200 bg-white rounded-md p-4 hover:border-blue-900 transition-colors"
                   data-testid={`library-item-${e.title.slice(0,20).replace(/\W+/g,'-').toLowerCase()}`}>
                  <div className="font-semibold text-slate-900">{e.title}</div>
                  <div className="mt-2 text-sm text-slate-600 leading-relaxed">{e.summary}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-blue-900">
                    Open source <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </div>
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
